import React from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    Tooltip,
    TooltipArrow,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@rocco/components/tooltip';
import type { ComponentPropsWithoutRef } from 'react';
import type * as TooltipPrimitive from '@radix-ui/react-tooltip';

interface BoostButtonProps {
    /** Whether the image has already been upscaled */
    wasUpscaled?: boolean;
    /** Handler for the boost click */
    onClick?: () => void;
    /** Optional className override for the button */
    className?: string;
    /** Optional tooltip text; when null the tooltip is not rendered */
    tooltipText?: string | null;
    /** Position of the tooltip relative to the button */
    tooltipPosition?: ComponentPropsWithoutRef<
        typeof TooltipPrimitive.Content
    >['side'];
    /** Custom trigger element; when provided, replaces the default ButtonContent */
    children?: React.ReactNode;
}

/** Forwards ref so Radix `asChild` on TooltipTrigger can attach to the DOM element. */
const ButtonContent = React.forwardRef<
    HTMLButtonElement,
    Pick<BoostButtonProps, 'wasUpscaled' | 'onClick' | 'className'>
>(({ wasUpscaled, onClick, className, ...rest }, ref) => (
    <button
        ref={ref}
        type="button"
        className={cn(
            'flex h-9 cursor-pointer items-center gap-2 rounded-full border border-black/10 text-sm font-medium leading-none backdrop-blur-xl [&_svg]:inline-block [&_svg]:shrink-0',
            wasUpscaled
                ? 'bg-white/70 px-2 text-black/70 hover:bg-white/90'
                : 'bg-gray-100/50 px-4 text-black hover:bg-gray-100',
            className,
        )}
        onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClick?.();
        }}
        {...rest}
    >
        <Sparkles className="h-4 w-4" />
        {wasUpscaled ? '' : 'Boost Resolution'}
    </button>
));

ButtonContent.displayName = 'ButtonContent';

/**
 * Shared "Boost Resolution" / "Resolution Boosted" button used across
 * single-image and carousel boost contexts.
 * When `children` is provided, renders them as the trigger element instead of
 * the default ButtonContent — useful for wrapping arbitrary buttons with a tooltip.
 */
export const BoostButton = ({
    wasUpscaled = false,
    onClick,
    className,
    tooltipText = null,
    tooltipPosition = 'bottom',
    children,
}: BoostButtonProps) => {
    const resolvedTooltipText = children
        ? (tooltipText ?? null)
        : (tooltipText ??
          (wasUpscaled
              ? 'Click to compare the original image with new image'
              : null));

    const trigger = children ?? (
        <ButtonContent
            wasUpscaled={wasUpscaled}
            onClick={onClick}
            className={className}
        />
    );

    if (!resolvedTooltipText) {
        return <>{trigger}</>;
    }

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>{trigger}</TooltipTrigger>
                <TooltipContent
                    side={tooltipPosition}
                    sideOffset={2}
                    className="!z-[9999] !max-w-[220px] !animate-none !rounded-none !bg-[#343434] !px-4 !py-4 !text-base !text-[#F8F9FA]"
                >
                    {resolvedTooltipText}
                    <TooltipArrow
                        width={12}
                        height={6}
                        className="fill-[#343434]"
                    />
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};
